package com.spground.spaceplaygroundwallpaper

import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import kotlin.math.roundToInt

/**
 * Web stand-in for WallpaperSettingsActivity: a slide-out panel over the scene
 * that writes the same localStorage keys the engine reads.
 *
 * Sections and ranges mirror the Android tabs so a value tuned here transfers
 * to the app unchanged. Keys the web engine has no equivalent for (max FPS,
 * gyro tilt, clock, Material You theming) are deliberately absent.
 */
class SettingsPanel(private val engine: SpaceSceneEngine) {

    /** Settings consumed by initScene()/initGravityWells() only take effect on rebuild. */
    private sealed interface Row {
        data class Subheading(val title: String) : Row
        data class Slider(
            val label: String,
            val key: String,
            val def: Int,
            val min: Int,
            val max: Int,
            val suffix: String = "",
            val divisor: Int = 1,
            val rebuild: Boolean = false,
        ) : Row
        data class Toggle(val label: String, val key: String, val def: Boolean, val rebuild: Boolean = false) : Row
        data object GlassColor : Row
    }

    private data class Section(val title: String, val rows: List<Row>)

    private val sections = listOf(
        Section("Planets", listOf(
            Row.Toggle("Gravity", WallpaperSettings.KEY_GRAVITY_ENABLED, WallpaperSettings.DEF_GRAVITY_ENABLED),
            Row.Toggle("Planets", WallpaperSettings.KEY_SHOW_PLANETS, WallpaperSettings.DEF_SHOW_PLANETS),
            Row.Toggle("Orbit lines", WallpaperSettings.KEY_SHOW_GRAVITY_FIELD, WallpaperSettings.DEF_SHOW_GRAVITY_FIELD),
            Row.Toggle("Collision with planets", WallpaperSettings.KEY_PLANET_COLLISION, WallpaperSettings.DEF_PLANET_COLLISION),
            Row.Subheading("Appearance"),
            Row.Slider("Planets", WallpaperSettings.KEY_PLANET_COUNT, WallpaperSettings.DEF_PLANET_COUNT, 1, 60, rebuild = true),
            Row.Slider("Planet size", WallpaperSettings.KEY_PLANET_SIZE, WallpaperSettings.DEF_PLANET_SIZE, 1, 600, rebuild = true),
            Row.Slider("Rotation speed", WallpaperSettings.KEY_PLANET_ROTATION, WallpaperSettings.DEF_PLANET_ROTATION, 0, 1200, suffix = "%"),
            Row.Slider("Neon glow", WallpaperSettings.KEY_PLANET_GLOW, WallpaperSettings.DEF_PLANET_GLOW, 0, 600, suffix = "%"),
            Row.Subheading("Motion"),
            Row.Slider("Wobble", WallpaperSettings.KEY_PLANET_WOBBLE, WallpaperSettings.DEF_PLANET_WOBBLE, 0, 400, suffix = "%"),
            Row.Subheading("Gravity field"),
            Row.Slider("Strength", WallpaperSettings.KEY_GRAVITY_STRENGTH, WallpaperSettings.DEF_GRAVITY_STRENGTH, 0, 10000, rebuild = true),
            Row.Slider("Field radius", WallpaperSettings.KEY_GRAVITY_RADIUS, WallpaperSettings.DEF_GRAVITY_RADIUS, 1, 600, rebuild = true),
            Row.Subheading("Orbit lines"),
            Row.Slider("Thickness", WallpaperSettings.KEY_GRAVITY_BORDER_THICKNESS, WallpaperSettings.DEF_GRAVITY_BORDER_THICKNESS, 1, 300),
            Row.Slider("Brightness", WallpaperSettings.KEY_GRAVITY_BORDER_BRIGHTNESS, WallpaperSettings.DEF_GRAVITY_BORDER_BRIGHTNESS, 1, 300),
            Row.Subheading("Placement"),
            Row.Slider("Spread", WallpaperSettings.KEY_CENTER_CLEAR, WallpaperSettings.DEF_CENTER_CLEAR, 0, 300, rebuild = true),
        )),
        Section("Rockets", listOf(
            Row.Subheading("Flight"),
            Row.Slider("Speed", WallpaperSettings.KEY_ROCKET_SPEED, WallpaperSettings.DEF_ROCKET_SPEED, 1, 1500),
            Row.Slider("Spawn interval", WallpaperSettings.KEY_ROCKET_SPAWN_INTERVAL, WallpaperSettings.DEF_ROCKET_SPAWN_INTERVAL, 1, 600, suffix = "s", divisor = 10),
            Row.Slider("Max concurrent (0 = unlimited)", WallpaperSettings.KEY_MAX_LIVE_ROCKETS, WallpaperSettings.DEF_MAX_LIVE_ROCKETS, 0, 500),
            Row.Subheading("Appearance"),
            Row.Slider("Size", WallpaperSettings.KEY_ROCKET_SIZE, WallpaperSettings.DEF_ROCKET_SIZE, 5, 600, suffix = "%"),
            Row.Slider("Neon glow", WallpaperSettings.KEY_ROCKET_GLOW, WallpaperSettings.DEF_ROCKET_GLOW, 0, 600, suffix = "%"),
            Row.Subheading("Trails"),
            Row.Slider("Trail lifetime", WallpaperSettings.KEY_PARTICLE_LIFETIME, WallpaperSettings.DEF_PARTICLE_LIFETIME, 1, 300, suffix = "s", divisor = 10),
            Row.Subheading("Collisions & debris"),
            Row.Slider("Debris pieces", WallpaperSettings.KEY_DEBRIS_COUNT, WallpaperSettings.DEF_DEBRIS_COUNT, 1, 300),
            Row.Slider("Debris lifetime", WallpaperSettings.KEY_DEBRIS_LIFETIME, WallpaperSettings.DEF_DEBRIS_LIFETIME, 1, 600, suffix = "s", divisor = 10),
            Row.Slider("Debris spin", WallpaperSettings.KEY_DEBRIS_SPIN, WallpaperSettings.DEF_DEBRIS_SPIN, 0, 1200, suffix = "%"),
            Row.Slider("Debris glow", WallpaperSettings.KEY_DEBRIS_GLOW, WallpaperSettings.DEF_DEBRIS_GLOW, 0, 600, suffix = "%"),
        )),
        Section("Stars", listOf(
            Row.Subheading("Starfield"),
            Row.Slider("Stars", WallpaperSettings.KEY_STAR_COUNT, WallpaperSettings.DEF_STAR_COUNT, 0, 10000, rebuild = true),
            Row.Slider("Size", WallpaperSettings.KEY_STAR_SIZE, WallpaperSettings.DEF_STAR_SIZE, 5, 1000, suffix = "%", rebuild = true),
            Row.Slider("Glow", WallpaperSettings.KEY_STAR_GLOW, WallpaperSettings.DEF_STAR_GLOW, 0, 1000, suffix = "%"),
            Row.Slider("Scroll speed", WallpaperSettings.KEY_STAR_SCROLL, WallpaperSettings.DEF_STAR_SCROLL, 0, 1500, suffix = "%"),
            Row.Subheading("Constellations"),
            Row.Toggle("Constellations", WallpaperSettings.KEY_CONSTELLATIONS_ENABLED, WallpaperSettings.DEF_CONSTELLATIONS_ENABLED, rebuild = true),
            Row.Subheading("Shooting stars"),
            Row.Slider("Count", WallpaperSettings.KEY_SHOOTING_STAR_COUNT, WallpaperSettings.DEF_SHOOTING_STAR_COUNT, 0, 100, rebuild = true),
            Row.Slider("Trail length", WallpaperSettings.KEY_SHOOTING_STAR_LENGTH, WallpaperSettings.DEF_SHOOTING_STAR_LENGTH, 0, 1000, suffix = "%"),
            Row.Slider("Brightness", WallpaperSettings.KEY_SHOOTING_STAR_BRIGHTNESS, WallpaperSettings.DEF_SHOOTING_STAR_BRIGHTNESS, 0, 1000, suffix = "%"),
            Row.Subheading("Galaxies"),
            Row.Toggle("Spiral galaxies", WallpaperSettings.KEY_GALAXIES_ENABLED, WallpaperSettings.DEF_GALAXIES_ENABLED, rebuild = true),
            Row.Slider("Count", WallpaperSettings.KEY_SPIRAL_GALAXY_COUNT, WallpaperSettings.DEF_SPIRAL_GALAXY_COUNT, 0, 12, rebuild = true),
        )),
        Section("Effects", listOf(
            Row.Toggle("Sprite art", WallpaperSettings.KEY_USE_SPRITES, WallpaperSettings.DEF_USE_SPRITES),
            Row.Toggle("OLED black", WallpaperSettings.KEY_TRUE_BLACK, WallpaperSettings.DEF_TRUE_BLACK),
            Row.Toggle("Low power", WallpaperSettings.KEY_LOW_POWER, WallpaperSettings.DEF_LOW_POWER),
            Row.Subheading("Readability"),
            Row.Slider("Dark scrim", WallpaperSettings.KEY_DARK_SCRIM, WallpaperSettings.DEF_DARK_SCRIM, 0, 100),
            Row.Slider("Large element dimming", WallpaperSettings.KEY_ELEMENT_DIM, WallpaperSettings.DEF_ELEMENT_DIM, 0, 100),
            Row.Subheading("Glass overlay"),
            Row.Slider("Tint intensity", WallpaperSettings.KEY_GLASS_TINT, WallpaperSettings.DEF_GLASS_TINT, 0, 100),
            Row.Slider("Gradient strength", WallpaperSettings.KEY_GLASS_GRADIENT, WallpaperSettings.DEF_GLASS_GRADIENT, 0, 100),
            Row.GlassColor,
            Row.Slider("Tint opacity", WallpaperSettings.KEY_GLASS_ALPHA, WallpaperSettings.DEF_GLASS_ALPHA, 0, 255),
            Row.Subheading("Warp (click the scene)"),
            Row.Slider("Duration", WallpaperSettings.KEY_WARP_DURATION, WallpaperSettings.DEF_WARP_DURATION, 1, 300, suffix = "s", divisor = 10),
            Row.Slider("Intensity", WallpaperSettings.KEY_WARP_INTENSITY, WallpaperSettings.DEF_WARP_INTENSITY, 0, 1000, suffix = "%"),
            Row.Subheading("Motion"),
            Row.Slider("Parallax", WallpaperSettings.KEY_PARALLAX_AMOUNT, WallpaperSettings.DEF_PARALLAX_AMOUNT, 0, 300, suffix = "%"),
        )),
    )

    private lateinit var panel: HTMLElement
    private var isOpen = false

    fun install() {
        document.head!!.appendChild(document.createElement("style").apply { textContent = CSS })

        val toggleButton = el("button", "sw-fab").apply {
            textContent = "⚙"
            setAttribute("title", "Settings (S)")
            setAttribute("aria-label", "Settings")
        }
        toggleButton.addEventListener("click", { e -> e.stopPropagation(); setOpen(!isOpen) })

        panel = el("aside", "sw-panel")
        // The scene turns any stray click into a warp; the panel keeps its own.
        panel.addEventListener("click", { e -> e.stopPropagation() })
        panel.appendChild(buildHeader())
        val body = el("div", "sw-body")
        sections.forEach { body.appendChild(buildSection(it)) }
        panel.appendChild(body)

        document.body!!.appendChild(toggleButton)
        document.body!!.appendChild(panel)

        document.addEventListener("keydown", { e ->
            when (e.asDynamic().key as String) {
                "s", "S" -> setOpen(!isOpen)
                "Escape" -> setOpen(false)
            }
        })
    }

    private fun setOpen(value: Boolean) {
        isOpen = value
        if (value) panel.classList.add("open") else panel.classList.remove("open")
    }

    private fun buildHeader(): HTMLElement {
        val header = el("div", "sw-header")
        header.appendChild(el("h1").apply { textContent = "Settings" })

        val reset = el("button", "sw-btn").apply { textContent = "Defaults" }
        reset.addEventListener("click", {
            WallpaperSettings.clearAll()
            syncInputs()
            engine.reset()
        })

        val rebuild = el("button", "sw-btn").apply { textContent = "Rebuild" }
        rebuild.addEventListener("click", { engine.reset() })

        val close = el("button", "sw-btn").apply { textContent = "✕" }
        close.addEventListener("click", { setOpen(false) })

        val actions = el("div", "sw-actions")
        actions.appendChild(reset)
        actions.appendChild(rebuild)
        actions.appendChild(close)
        header.appendChild(actions)
        return header
    }

    private fun buildSection(section: Section): HTMLElement {
        val wrapper = el("section", "sw-section")
        wrapper.appendChild(el("h2").apply { textContent = section.title })
        section.rows.forEach { wrapper.appendChild(buildRow(it)) }
        return wrapper
    }

    private fun buildRow(row: Row): HTMLElement = when (row) {
        is Row.Subheading -> el("h3", "sw-sub").apply { textContent = row.title }
        is Row.Slider -> buildSlider(row)
        is Row.Toggle -> buildToggle(row)
        is Row.GlassColor -> buildGlassColor()
    }

    // Inputs are re-read from storage by "Defaults", so every control registers
    // how to restore itself rather than being rebuilt.
    private val syncers = mutableListOf<() -> Unit>()

    private fun syncInputs() = syncers.forEach { it() }

    private fun buildSlider(spec: Row.Slider): HTMLElement {
        val rowEl = el("div", "sw-row")
        val label = el("label", "sw-label").apply { textContent = spec.label }
        val value = el("span", "sw-value")
        val input = document.createElement("input") as HTMLInputElement
        input.type = "range"
        input.min = spec.min.toString()
        input.max = spec.max.toString()
        input.step = "1"

        fun render(raw: Int) {
            value.textContent = if (spec.divisor == 1) {
                "$raw${spec.suffix}"
            } else {
                "${(raw.toDouble() / spec.divisor * 10).roundToInt() / 10.0}${spec.suffix}"
            }
        }

        fun load() {
            val current = WallpaperSettings.getInt(spec.key, spec.def)
            input.value = current.toString()
            render(current)
        }
        load()
        syncers.add(::load)

        input.addEventListener("input", {
            val raw = input.value.toIntOrNull() ?: spec.def
            WallpaperSettings.setInt(spec.key, raw)
            render(raw)
        })
        // Rebuilding mid-drag would restart the scene on every pixel of travel.
        if (spec.rebuild) input.addEventListener("change", { engine.reset() })

        val head = el("div", "sw-head")
        head.appendChild(label)
        head.appendChild(value)
        rowEl.appendChild(head)
        rowEl.appendChild(input)
        return rowEl
    }

    private fun buildToggle(spec: Row.Toggle): HTMLElement {
        val rowEl = el("label", "sw-toggle")
        val input = document.createElement("input") as HTMLInputElement
        input.type = "checkbox"

        fun load() { input.checked = WallpaperSettings.getBoolean(spec.key, spec.def) }
        load()
        syncers.add(::load)

        input.addEventListener("change", {
            WallpaperSettings.setBoolean(spec.key, input.checked)
            if (spec.rebuild) engine.reset()
        })
        rowEl.appendChild(input)
        rowEl.appendChild(el("span").apply { textContent = spec.label })
        return rowEl
    }

    private fun buildGlassColor(): HTMLElement {
        val rowEl = el("div", "sw-row sw-color")
        val input = document.createElement("input") as HTMLInputElement
        input.type = "color"

        fun load() {
            val r = WallpaperSettings.getInt(WallpaperSettings.KEY_GLASS_RED, WallpaperSettings.DEF_GLASS_RED)
            val g = WallpaperSettings.getInt(WallpaperSettings.KEY_GLASS_GREEN, WallpaperSettings.DEF_GLASS_GREEN)
            val b = WallpaperSettings.getInt(WallpaperSettings.KEY_GLASS_BLUE, WallpaperSettings.DEF_GLASS_BLUE)
            input.value = "#" + listOf(r, g, b).joinToString("") { it.toString(16).padStart(2, '0') }
        }
        load()
        syncers.add(::load)

        input.addEventListener("input", {
            val hex = input.value.removePrefix("#")
            WallpaperSettings.setInt(WallpaperSettings.KEY_GLASS_RED, hex.substring(0, 2).toInt(16))
            WallpaperSettings.setInt(WallpaperSettings.KEY_GLASS_GREEN, hex.substring(2, 4).toInt(16))
            WallpaperSettings.setInt(WallpaperSettings.KEY_GLASS_BLUE, hex.substring(4, 6).toInt(16))
        })

        val head = el("div", "sw-head")
        head.appendChild(el("label", "sw-label").apply { textContent = "Tint color" })
        head.appendChild(input)
        rowEl.appendChild(head)
        return rowEl
    }

    private fun el(tag: String, className: String? = null): HTMLElement =
        (document.createElement(tag) as HTMLElement).apply { className?.let { this.className = it } }

    private companion object {
        const val CSS = """
.sw-fab {
    position: fixed; top: 16px; right: 16px; z-index: 10;
    width: 44px; height: 44px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,.18);
    background: rgba(18,14,38,.72); color: #fff;
    font-size: 20px; line-height: 1; cursor: pointer;
    backdrop-filter: blur(8px);
}
.sw-fab:hover { background: rgba(40,30,80,.85); }
.sw-panel {
    position: fixed; top: 0; right: 0; z-index: 11;
    width: min(360px, 92vw); height: 100%;
    display: flex; flex-direction: column;
    background: rgba(10,8,24,.92); color: #eee;
    border-left: 1px solid rgba(255,255,255,.12);
    backdrop-filter: blur(14px);
    font: 14px/1.4 system-ui, -apple-system, sans-serif;
    transform: translateX(100%); transition: transform .22s ease;
}
.sw-panel.open { transform: none; }
.sw-header {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    padding: 14px 16px; border-bottom: 1px solid rgba(255,255,255,.12);
}
.sw-header h1 { margin: 0; font-size: 16px; font-weight: 600; }
.sw-actions { display: flex; gap: 6px; }
.sw-btn {
    border: 1px solid rgba(255,255,255,.18); border-radius: 10px;
    background: rgba(40,30,70,.8); color: #fff;
    padding: 6px 10px; font-size: 12px; cursor: pointer;
}
.sw-btn:hover { background: rgba(70,50,120,.9); }
.sw-body { overflow-y: auto; padding: 8px 16px 32px; overscroll-behavior: contain; }
.sw-section { padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,.08); }
.sw-section h2 { margin: 4px 0 8px; font-size: 13px; letter-spacing: .08em; text-transform: uppercase; color: #9f8fff; }
.sw-sub { margin: 14px 0 4px; font-size: 12px; font-weight: 600; color: rgba(255,255,255,.55); }
.sw-row { margin: 8px 0; }
.sw-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.sw-label { font-size: 13px; }
.sw-value { font-size: 12px; color: rgba(255,255,255,.6); font-variant-numeric: tabular-nums; }
.sw-row input[type=range] { width: 100%; accent-color: #7a5cff; margin: 4px 0 0; }
.sw-toggle { display: flex; align-items: center; gap: 8px; margin: 6px 0; font-size: 13px; cursor: pointer; }
.sw-toggle input { accent-color: #7a5cff; width: 16px; height: 16px; }
.sw-color input[type=color] { width: 44px; height: 26px; padding: 0; border: 1px solid rgba(255,255,255,.2); background: none; cursor: pointer; }
"""
    }
}
